import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Plus, Eye, Pencil, Search } from "@/components/icons";
import api from "@/lib/api";
import { Page } from "@/components/PageConsole";
import { useAuth } from "@/context/AuthContext";
import { getErrorMessage } from "@/lib/errors";
import {
  LOCATION_TYPE_LABELS,
  LOCATION_STATUS_LABELS,
  OWNERSHIP_LABELS,
} from "@/components/Badges";
import { EmptyState } from "@/components/ListState";
import { balkMismatches } from "@/lib/balkState";
import { plural } from "@/lib/plural";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";

export default function LocationsList() {
  const navigate = useNavigate();
  const { canWrite } = useAuth();
  const [locations, setLocations] = useState([]);
  const [ruimtes, setRuimtes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [searchParams, setSearchParams] = useSearchParams();
  const [statusFilter, setStatusFilter] = useState(searchParams.get("status") || "all");
  const [typeFilter, setTypeFilter] = useState(searchParams.get("type") || "all");
  const [search, setSearch] = useState(searchParams.get("q") || "");

  useEffect(() => {
    const p = {};
    if (statusFilter !== "all") p.status = statusFilter;
    if (typeFilter !== "all") p.type = typeFilter;
    if (search.trim()) p.q = search.trim();
    setSearchParams(p, { replace: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, typeFilter, search]);

  const load = async () => {
    setLoading(true);
    setLoadError("");
    try {
      // A failed /ruimtes load is its own state: without it every location would
      // read "0 sporen", and a failure that renders as zero is the balk lesson.
      const [{ data }, rui] = await Promise.all([
        api.get("/locations"),
        api.get("/ruimtes"),
      ]);
      setLocations(data);
      setRuimtes(rui.data);
    } catch (err) {
      setLoadError(getErrorMessage(err, "Locaties konden niet geladen worden."));
      setLocations([]);
      setRuimtes([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    return locations.filter((l) => {
      if (statusFilter !== "all" && l.status !== statusFilter) return false;
      if (typeFilter !== "all" && l.type !== typeFilter) return false;
      if (search) {
        const q = search.toLowerCase();
        if (
          !l.name.toLowerCase().includes(q) &&
          !l.location_id.toLowerCase().includes(q) &&
          !(l.address?.city || "").toLowerCase().includes(q)
        )
          return false;
      }
      return true;
    });
  }, [locations, statusFilter, typeFilter, search]);

  // Sporen live on the was-hallen of a location now, so they are counted there.
  const sporenAt = (locationId) => ruimtes
    .filter((r) => r.location_id === locationId)
    .reduce((n, r) => n + (r.sporen?.length || 0), 0);

  const facts = loadError
    ? ["locaties konden niet geladen worden"]
    : [`${locations.length} locaties`, `${locations.filter((l) => l.ownership === "huur").length} gehuurd`,
       plural(ruimtes.reduce((n, r) => n + (r.sporen?.length || 0), 0), "spoor", "sporen")];

  // Truckwash / Actief / Huur are the same on nearly every row, so they are not
  // columns. What deviates from the majority gets ink; what everyone shares does
  // not. The majority is measured on the whole register, not on the filtered set,
  // so a filter cannot make "normal" flip meaning.
  const majority = useMemo(() => {
    const top = (pick) => {
      const counts = {};
      locations.forEach((l) => { const v = pick(l); if (v) counts[v] = (counts[v] || 0) + 1; });
      return Object.entries(counts).sort((a, b) => b[1] - a[1])[0]?.[0] || null;
    };
    return {
      type: top((l) => l.type),
      status: top((l) => l.status),
      ownership: top((l) => l.ownership),
    };
  }, [locations]);

  const exceptions = (l) => [
    l.type && l.type !== majority.type ? LOCATION_TYPE_LABELS[l.type] : null,
    l.status && l.status !== majority.status ? LOCATION_STATUS_LABELS[l.status] : null,
    l.ownership && l.ownership !== majority.ownership ? OWNERSHIP_LABELS[l.ownership] : null,
    (l.category_lifetime_overrides || []).length
      ? `${l.category_lifetime_overrides.length}× eigen levensduur`
      : null,
  ].filter(Boolean);

  const actions = canWrite()
    ? [{ key: "nieuw", label: "Nieuwe locatie", icon: Plus, variant: "primary",
         testid: "locations-create-button", onClick: () => navigate("/locaties/nieuw") }]
    : [];

  // A truckwash that cannot be placed in de balk is a register error, not a
  // machine state. It is named here — with the site and what is wrong with it —
  // instead of lighting a 6px segment that nobody can act on.
  const mismatches = useMemo(() => balkMismatches(locations), [locations]);

  return (
    <Page console={{ variant: "view", title: "Locaties", facts, actions }}>

      {!loadError && mismatches.length > 0 && (
        <Card
          data-testid="balk-mismatches"
          className="mb-4 border-letop-rand bg-letop-vlak p-3"
        >
          <p className="text-label uppercase text-letop-tekst">
            {mismatches.length === 1 ? "1 truckwash mist zijn plek in de balk" : `${mismatches.length} truckwashlocaties missen hun plek in de balk`}
          </p>
          <ul className="mt-2 space-y-1">
            {mismatches.map((m) => (
              <li key={`${m.id}-${m.reason}`} data-testid={`balk-mismatch-${m.id}`} className="text-body text-letop-tekst">
                <button
                  type="button"
                  onClick={() => navigate(`/locaties/${m.id}/bewerken`)}
                  className="font-medium underline decoration-letop-rand underline-offset-2"
                >
                  {m.name}
                </button>{" "}
                {m.reason}.
              </li>
            ))}
          </ul>
        </Card>
      )}

      {loadError && (
        <Card className="flex flex-col items-center gap-3 py-12 text-center">
          <p data-testid="locations-load-error" className="text-body text-destructive">
            {loadError}
          </p>
          <Button variant="secondary" data-testid="locations-retry-button" onClick={load}>
            Opnieuw proberen
          </Button>
        </Card>
      )}

      {!loadError && (
      <Card>
        <div className="flex flex-wrap items-center gap-2 border-b border-border p-3">
          <div className="relative w-full sm:w-64">
            <Search
              size={18}
              className="absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              data-testid="locations-search-input"
              className="h-9 pl-8"
              placeholder="Zoek op naam of plaats"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="locations-status-filter">
              <SelectValue placeholder="Status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle statussen</SelectItem>
              <SelectItem value="active">Actief</SelectItem>
              <SelectItem value="planned">Gepland</SelectItem>
              <SelectItem value="closed">Gesloten</SelectItem>
            </SelectContent>
          </Select>
          <Select value={typeFilter} onValueChange={setTypeFilter}>
            <SelectTrigger className="h-9 w-40" data-testid="locations-type-filter">
              <SelectValue placeholder="Type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle types</SelectItem>
              <SelectItem value="truckwash">Truckwash</SelectItem>
              <SelectItem value="pand">Pand</SelectItem>
              <SelectItem value="opslag">Opslag</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {!loading && filtered.length === 0 ? (
          <EmptyState
            testid="locations-empty"
            className="border-0"
            title={locations.length === 0 ? "Nog geen locaties" : "Geen locatie past bij deze filters"}
            purpose={locations.length === 0
              ? "Elke wasstraat, pand en opslag staat hier met zijn sporen. Vanaf een locatie hangen de machines, de keuringen en wie wat betaalt met de verhuurder."
              : "Verruim je filters om de andere vestigingen weer te zien."}
            action={locations.length === 0
              ? (canWrite() ? { label: "Nieuwe locatie", icon: Plus, onClick: () => navigate("/locaties/nieuw") } : null)
              : { label: "Filters wissen", onClick: () => { setStatusFilter("all"); setTypeFilter("all"); setSearch(""); } }}
          />
        ) : (
        <>
        <div className="hidden sm:block">
          <Table data-testid="locations-table">
            <TableHeader>
              <TableRow>
                <TableHead>Naam</TableHead>
                <TableHead>Plaats</TableHead>
                <TableHead className="w-[100px]">Sporen</TableHead>
                <TableHead>Uitzonderingen</TableHead>
                <TableHead className="w-[120px] text-right">Acties</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading &&
                Array.from({ length: 6 }).map((_, i) => (
                  <TableRow key={i}>
                    <TableCell colSpan={5}>
                      <Skeleton className="h-6 w-full" />
                    </TableCell>
                  </TableRow>
                ))}
              {!loading &&
                filtered.map((l) => (
                  <TableRow
                    key={l.location_id}
                    data-testid={`location-row-${l.location_id}`}
                    onClick={() => navigate(`/locaties/${l.location_id}`)}
                    className="cursor-pointer hover:bg-muted/40"
                  >
                    <TableCell>
                      <div className="font-medium text-foreground">{l.name}</div>
                      {l.location_id !== l.name.toLowerCase().replace(/\s+/g, "-") && (
                        <div className="font-mono text-data text-muted-foreground">{l.location_id}</div>
                      )}
                    </TableCell>
                    <TableCell className="text-body">{l.address?.city !== l.name ? l.address?.city : ""}</TableCell>
                    <TableCell className="text-body tabular-nums">{sporenAt(l.location_id)}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap items-center gap-2" data-testid={`location-exceptions-${l.location_id}`}>
                        {exceptions(l).map((e) => (
                          <span key={e} className="text-data text-inkt-zacht">{e}</span>
                        ))}
                      </div>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          data-testid={`locations-row-action-view-${l.location_id}`}
                          onClick={() => navigate(`/locaties/${l.location_id}`)}
                          title="Bekijken"
                        >
                          <Eye size={18} />
                        </Button>
                        {canWrite() && (
                          <Button
                            variant="ghost"
                            size="icon"
                            data-testid={`locations-row-action-edit-${l.location_id}`}
                            onClick={() => navigate(`/locaties/${l.location_id}/bewerken`)}
                            title="Bewerken"
                          >
                            <Pencil size={18} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </div>

        {/* Mobile: stacked, tappable cards (identifier → plaats → uitzonderingen). */}
        <div className="divide-y divide-border sm:hidden" data-testid="locations-cardlist">
          {loading && Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="p-3"><Skeleton className="h-14 w-full" /></div>
          ))}
          {!loading && filtered.map((l) => (
            <div key={l.location_id} data-testid={`location-card-${l.location_id}`} className="flex items-start justify-between gap-3 p-3">
              <button
                type="button"
                onClick={() => navigate(`/locaties/${l.location_id}`)}
                className="min-w-0 flex-1 space-y-0.5 text-left transition-colors duration-150"
              >
                <div className="truncate font-medium text-foreground">{l.name}</div>
                {/* The code only earns a line when it says something the name does not. */}
                {l.location_id !== l.name.toLowerCase().replace(/\s+/g, "-") && (
                  <div className="font-mono text-data text-muted-foreground">{l.location_id}</div>
                )}
                <div className="text-data text-muted-foreground">
                  {[l.address?.city && l.address.city !== l.name ? l.address.city : null,
                    plural(sporenAt(l.location_id), "spoor", "sporen")].filter(Boolean).join(" · ")}
                </div>
                {exceptions(l).length > 0 && (
                  <div className="pt-1 text-data text-inkt-zacht">{exceptions(l).join(" · ")}</div>
                )}
              </button>
              {canWrite() && (
                <Button
                  variant="ghost"
                  size="icon"
                  data-testid={`locations-card-action-edit-${l.location_id}`}
                  onClick={() => navigate(`/locaties/${l.location_id}/bewerken`)}
                  title="Bewerken"
                >
                  <Pencil size={18} />
                </Button>
              )}
            </div>
          ))}
        </div>
        </>
        )}
      </Card>
      )}
    </Page>
  );
}
